// resources/js/Pages/Formula/PropertiesPane.js - UPDATED FOR FLAT + ARRAYS DESIGN
import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Form, Alert } from 'react-bootstrap'; // ✅ Import Form and Alert

export default function PropertiesPane({ node, variables, onChange, onConvertToArray }) {
  if (!node) {
    return (
      <div className="card mt-3">
        <div className="card-header">Properties</div>
        <div className="card-body">
          <div className="text-muted">Select a node to edit its properties</div>
        </div>
      </div>
    );
  }
  
  const [local, setLocal] = useState(node);
  const textareaRef = useRef(null);
  const isRoot = node.key === 'Root' || node.type === 'root';
  const isField = node.type === 'field';
  const isArray = node.type === 'array' || node.type === 'list';
  const isGraph = node.type === 'graph';
  const commitTimeoutRef = useRef(null);

  useEffect(() => setLocal(node), [node]);

  // ✅ Debounced commit function
  const debouncedCommit = (patch) => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
    }
    
    commitTimeoutRef.current = setTimeout(() => {
      onChange(node.id, patch);
    }, 300);
  };

  // ✅ Immediate commit (for blur events)
  const immediateCommit = (patch) => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
    }
    onChange(node.id, patch);
  };

  const insertVar = key => {
    if (!textareaRef.current) return;
    const { selectionStart, selectionEnd, value } = textareaRef.current;
    const next =
      value.slice(0, selectionStart) + `{{${key}}}` + value.slice(selectionEnd);
    
    setLocal({ ...local, instruction: next });
    immediateCommit({ instruction: next });
  };

  // ✅ Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
    };
  }, []);

  // ✅ Field type options
  const fieldTypeOptions = [
    { value: 'string', label: '📝 Text', description: 'General text or numeric content' },
    { value: 'score', label: '💯 Score (1-10)', description: 'Displays a number with a color grade' },
    { value: 'importance', label: '⚠️ Importance Badge', description: 'Shows importance level with colored badge (1-5)' },
    { value: 'badge', label: '🏷️ Custom Badge', description: 'Custom colored badge using badgeMap (define value→color pairs)' },
    { value: 'scene_number', label: '🎬 Scene Link', description: 'Single scene reference with link' },
    { value: 'scene_list', label: '📋 Scene List', description: 'Multiple scene references' },
    { value: 'questions_for_llm', label: '🤖 AI Follow-up Questions', description: 'Generates relevant questions for deeper analysis' }
  ];

  // ✅ Style preset options for visual emphasis
  const stylePresets = [
    { value: '', label: 'Default', icon: '—', description: 'No special styling', preview: null },
    { value: 'warning', label: 'Warning', icon: '⚠️', description: 'Amber — something writers should heed', preview: { bg: '#fff8e1', border: '#FF9800', text: '#E65100' } },
    { value: 'danger', label: 'Danger', icon: '🚨', description: 'Red — critical issue requiring attention', preview: { bg: '#fdecea', border: '#f44336', text: '#c62828' } },
    { value: 'info', label: 'Info', icon: 'ℹ️', description: 'Blue — informational context', preview: { bg: '#e8f4fd', border: '#2196F3', text: '#1565C0' } },
    { value: 'success', label: 'Success', icon: '✅', description: 'Green — positive signal or strength', preview: { bg: '#e8f5e9', border: '#4CAF50', text: '#2E7D32' } },
    { value: 'action', label: 'Action', icon: '🎯', description: 'Purple — actionable recommendation', preview: { bg: '#f3e5f5', border: '#9C27B0', text: '#6A1B9A' } },
    { value: 'neutral', label: 'Neutral', icon: '📌', description: 'Gray — supplementary context', preview: { bg: '#f5f5f5', border: '#78909C', text: '#37474F' } },
  ];

  // ✅ Get current field type with fallback
  const getCurrentFieldType = () => {
    return local.valueType || 'string';
  };

  // ✅ ROOT node properties
  if (isRoot) {
    return (
      <div className="card mt-3">
        <div className="card-header">Properties</div>
        <div className="card-body">
          <div className="text-center text-muted">
            <div className="mb-3" style={{ fontSize: '2rem' }}>🏠</div>
            <h6>Root Container</h6>
            <p className="small mb-0">
              The root container holds all your fields in sequence. 
              Add fields to define your response structure.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ FIELD node properties
  if (isField) {
    return (
      <div className="card mt-3">
        <div className="card-header">
          <div className="d-flex align-items-center">
            <span className="me-2">📝</span>
            Field Properties
          </div>
        </div>
        <div className="card-body">

          {/* Key */}
          <div className="mb-3">
            <label className="form-label">Field Name</label>
            <input
              className="form-control form-control-sm"
              value={local.key}
              onChange={e => {
                setLocal({ ...local, key: e.target.value });
                debouncedCommit({ key: e.target.value });
              }}
              onBlur={e => {
                immediateCommit({ key: e.target.value });
              }}
              placeholder="e.g., title, genre_score, character_name"
            />
            <small className="form-text text-muted">
              This becomes a property in the JSON response
            </small>
          </div>

          {/* Field Type */}
          <div className="mb-3">
            <label className="form-label">Field Type</label>
            <select
              className="form-select form-select-sm"
              value={getCurrentFieldType()}
              onChange={e => {
                const newType = e.target.value;
                setLocal({ ...local, valueType: newType });
                immediateCommit({ valueType: newType });
              }}
            >
              {fieldTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small className="form-text text-muted">
              {fieldTypeOptions.find(opt => opt.value === getCurrentFieldType())?.description}
            </small>
          </div>

          {/* ✅ Style Preset */}
          <div className="mb-3">
            <label className="form-label">Display Style</label>
            <div className="row g-2">
              {stylePresets.map(preset => {
                const isSelected = (local.style || '') === preset.value;
                return (
                  <div key={preset.value || '_default'} className="col-4">
                    <button
                      className={`btn w-100 btn-sm ${isSelected ? 'btn-dark' : 'btn-outline-secondary'}`}
                      style={preset.preview && !isSelected ? {
                        backgroundColor: preset.preview.bg,
                        borderColor: preset.preview.border,
                        color: preset.preview.text,
                      } : {}}
                      onClick={() => {
                        setLocal({ ...local, style: preset.value });
                        immediateCommit({ style: preset.value });
                      }}
                      title={preset.description}
                    >
                      <div style={{ fontSize: '1rem' }}>{preset.icon}</div>
                      <small>{preset.label}</small>
                    </button>
                  </div>
                );
              })}
            </div>
            <small className="form-text text-muted">
              {stylePresets.find(p => p.value === (local.style || ''))?.description || 'Choose a visual style for this field in the output.'}
            </small>
          </div>

          {/* Bold toggle (useful for first column in tables) */}
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="field-bold"
              checked={!!local.bold}
              onChange={e => {
                setLocal({ ...local, bold: e.target.checked });
                immediateCommit({ bold: e.target.checked });
              }}
            />
            <label className="form-check-label" htmlFor="field-bold">
              Bold text
            </label>
            <br />
            <small className="form-text text-muted">
              Makes this column's cell text bold in table output
            </small>
          </div>

          {/* Field Instruction */}
          <div className="mb-3">
            <label className="form-label">
              Field Instruction
              <Dropdown className="d-inline ms-2" onSelect={insertVar}>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Insert Var
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {variables.map(v => (
                    <Dropdown.Item key={v.key} eventKey={v.key}>
                      {v.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </label>
            <textarea
              ref={textareaRef}
              className="form-control form-control-sm"
              rows="3"
              placeholder="Tell the AI what to put in this field..."
              value={local.instruction || ''}
              onChange={e => {
                setLocal({ ...local, instruction: e.target.value });
                debouncedCommit({ instruction: e.target.value });
              }}
              onBlur={e => {
                immediateCommit({ instruction: e.target.value });
              }}
            />
            <small className="form-text text-muted">
              This instruction tells the AI what content to generate for this field.
              {getCurrentFieldType() === 'score' && (
                <><br/><strong>Score Field:</strong> The AI will return a number 1-10 with color-coded display.</>
              )}
              {getCurrentFieldType() === 'importance' && (
                <><br/><strong>Importance Badge:</strong> The AI will return a 1-5 rating displayed as a colored badge.</>
              )}
              {getCurrentFieldType() === 'scene_number' && (
                <><br/><strong>Scene Link:</strong> The AI will return a single scene number with navigation link.</>
              )}
              {getCurrentFieldType() === 'scene_list' && (
                <><br/><strong>Scene List:</strong> The AI will return multiple scene numbers as an array.</>
              )}
              {getCurrentFieldType() === 'questions_for_llm' && (
                <><br/><strong>AI Follow-up Questions:</strong> The AI will generate relevant questions for deeper analysis.</>
              )}
            </small>
          </div>

          {/* ✅ NEW: Collapsible Switch */}
          <div className="mb-3">
            <Form.Check 
              type="switch" 
              id={`collapsible-prop-${local.id}`} 
              label="Collapsible Content" 
              checked={!!local.collapsible} 
              onChange={e => { 
                setLocal({ ...local, collapsible: e.target.checked }); 
                immediateCommit({ collapsible: e.target.checked }); 
              }}
            />
            <small className="form-text text-muted">If checked, long text will be truncated.</small>
          </div>

          {/* ✅ NEW: Convert to Array Section */}
          <div className="mt-4 pt-3 border-top">
            <h6 className="text-info mb-2">🔄 Convert to Array</h6>
            <p className="small text-muted mb-3">
              Want the AI to return <strong>multiple items</strong> instead of a single value? 
              Convert this field to an array.
            </p>
            
            <div className="row g-2">
              <div className="col-6">
                <button 
                  className="btn btn-outline-info btn-sm w-100"
                  onClick={() => onConvertToArray?.(node.id, 'table')}
                  title="Structured data in rows and columns"
                >
                  <div>📊</div>
                  <small>Table</small>
                </button>
              </div>
              <div className="col-6">
                <button 
                  className="btn btn-outline-info btn-sm w-100"
                  onClick={() => onConvertToArray?.(node.id, 'cards')}
                  title="Card-based layout"
                >
                  <div>🃏</div>
                  <small>Cards</small>
                </button>
              </div>
              <div className="col-6">
                <button 
                  className="btn btn-outline-info btn-sm w-100"
                  onClick={() => onConvertToArray?.(node.id, 'tabs')}
                  title="Tabbed interface"
                >
                  <div>📂</div>
                  <small>Tabs</small>
                </button>
              </div>
              <div className="col-6">
                <button 
                  className="btn btn-outline-info btn-sm w-100"
                  onClick={() => onConvertToArray?.(node.id, 'ul')}
                  title="Simple list items"
                >
                  <div>📋</div>
                  <small>List</small>
                </button>
              </div>
            </div>
            
            <small className="form-text text-muted mt-2">
              Arrays require you to define what each item represents (e.g., "each row represents a character").
            </small>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ARRAY node properties
  if (isArray) {
    const isLlmQuestionList = local.renderAs === 'llm_questions';

    // RENDER A SPECIAL UI FOR THE LLM QUESTIONS COMPONENT
    if (isLlmQuestionList) {
      return (
        <div className="card mt-3">
          <div className="card-header">
            <div className="d-flex align-items-center">
              <span className="me-2">🤖</span>
              AI Questions Properties
            </div>
          </div>
          <div className="card-body">
            <Alert variant="info">
              <Alert.Heading className="h6 mb-1">Interactive Element</Alert.Heading>
              <small>This special array instructs the AI to generate a list of strings. Each string will be rendered as a clickable 'Ask Assistant' button for the end-user.</small>
            </Alert>

            {/* Key */}
            <div className="mb-3">
              <label className="form-label">Key Name</label>
              <input
                className="form-control form-control-sm"
                value={local.key}
                onChange={e => {
                  setLocal({ ...local, key: e.target.value });
                  debouncedCommit({ key: e.target.value });
                }}
                onBlur={e => immediateCommit({ key: e.target.value })}
                placeholder="e.g., follow_up_questions, suggested_actions"
              />
            </div>

            {/* Instruction */}
            <div className="mb-3">
              <label className="form-label">Instruction</label>
              <textarea
                ref={textareaRef}
                className="form-control form-control-sm"
                rows="3"
                placeholder="e.g., 'Generate 5 thoughtful follow-up questions about this analysis'"
                value={local.instruction || ''}
                onChange={e => {
                  setLocal({ ...local, instruction: e.target.value });
                  debouncedCommit({ instruction: e.target.value });
                }}
                onBlur={e => immediateCommit({ instruction: e.target.value })}
              />
            </div>
          </div>
        </div>
      );
    }

    const displayOptions = {
      ul: { label: 'Simple List', icon: '📋' }, // ✅ UPDATED
      table: { label: 'Table', icon: '📊' },
      cards: { label: 'Cards', icon: '🃏' },
      tabs: { label: 'Tabs', icon: '�' }
    };

    return (
      <div className="card mt-3">
        <div className="card-header">
          <div className="d-flex align-items-center">
            <span className="me-2">{displayOptions[local.display]?.icon || '📋'}</span>
            Array Properties ({displayOptions[local.display]?.label || 'Simple List'})
          </div>
        </div>
        <div className="card-body">

          {/* Key */}
          <div className="mb-3">
            <label className="form-label">Array Name</label>
            <input
              className="form-control form-control-sm"
              value={local.key}
              onChange={e => {
                setLocal({ ...local, key: e.target.value });
                debouncedCommit({ key: e.target.value });
              }}
              onBlur={e => {
                immediateCommit({ key: e.target.value });
              }}
              placeholder="e.g., characters, scenes, recommendations"
            />
          </div>

          {/* Display Type */}
          <div className="mb-3">
            <label className="form-label">Display Type</label>
            <div className="row g-2">
              {Object.entries(displayOptions).map(([value, { label, icon }]) => (
                <div key={value} className="col-6">
                  <button
                    className={`btn w-100 ${local.display === value ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
                    onClick={() => {
                      setLocal({ ...local, display: value });
                      immediateCommit({ display: value });
                    }}
                  >
                    <div>{icon}</div>
                    <small>{label}</small>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ✅ REQUIRED: Array Instruction */}
          <div className={`mb-3 p-3 rounded ${!local.instruction ? 'bg-warning bg-opacity-10 border border-warning' : 'bg-success bg-opacity-10'}`}>
            <label className="form-label">
              <div className="d-flex align-items-center">
                <span className={`badge me-2 ${!local.instruction ? 'bg-warning text-dark' : 'bg-success'}`}>
                  {!local.instruction ? '⚠️ REQUIRED' : '✅ COMPLETE'}
                </span>
                <strong>Array Instruction</strong>
              </div>
              <Dropdown className="d-inline ms-2" onSelect={insertVar}>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Insert Var
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {variables.map(v => (
                    <Dropdown.Item key={v.key} eventKey={v.key}>
                      {v.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </label>
            <textarea
              ref={textareaRef}
              className={`form-control form-control-sm ${!local.instruction ? 'border-warning' : ''}`}
              rows="3"
              placeholder={
                local.display === 'cards' 
                  ? "e.g., 'Each card represents a main character in the screenplay'"
                  : local.display === 'table'
                  ? "e.g., 'Each row represents a scene that contains action sequences'"
                  : local.display === 'tabs'
                  ? "e.g., 'Each tab represents a story act (Act 1, Act 2, Act 3)'"
                  // ✅ UPDATED placeholder for simple list
                  : "e.g., 'A list of key suggestions for improvement...'"
              }
              value={local.instruction || ''}
              onChange={e => {
                setLocal({ ...local, instruction: e.target.value });
                debouncedCommit({ instruction: e.target.value });
              }}
              onBlur={e => {
                immediateCommit({ instruction: e.target.value });
              }}
            />
            <small className={`form-text ${!local.instruction ? 'text-warning' : 'text-success'}`}>
              <strong>
                {!local.instruction 
                  ? `⚠️ Required: What should each ${local.display === 'cards' ? 'card' : local.display === 'table' ? 'row' : local.display === 'tabs' ? 'tab' : 'item'} represent?`
                  : `✅ This tells the AI what multiple items to create`
                }
              </strong>
              <br/>
              This instruction is crucial - it prevents accidentally grouping unrelated fields.
            </small>
          </div>

          {/* ✅ NEW: Collapsible Switch */}
          <div className="mb-3">
            <Form.Check 
              type="switch" 
              id={`collapsible-prop-${local.id}`} 
              label="Collapsible Content" 
              checked={!!local.collapsible} 
              onChange={e => { 
                setLocal({ ...local, collapsible: e.target.checked }); 
                immediateCommit({ collapsible: e.target.checked }); 
              }}
            />
            <small className="form-text text-muted">If checked, the entire list/table will be collapsible.</small>
          </div>

          {/* ✅ NEW: Show Title Switch (only for ul lists) */}
          {local.display === 'ul' && (
            <div className="mb-3">
              <Form.Check 
                type="switch" 
                id={`show-title-prop-${local.id}`} 
                label="Display key as title" 
                checked={!!local.showTitle} 
                onChange={e => { 
                  setLocal({ ...local, showTitle: e.target.checked }); 
                  immediateCommit({ showTitle: e.target.checked }); 
                }}
              />
              <small className="form-text text-muted">If checked, the array's key will be displayed as a header above the list.</small>
            </div>
          )}

          {/* Info about children */}
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              <strong>💡 Array Structure:</strong> Add fields below to define what data each {local.display === 'cards' ? 'card' : local.display === 'table' ? 'row' : local.display === 'tabs' ? 'tab' : 'item'} contains.
              {local.display === 'tabs' && <><br/><strong>Tabs:</strong> First field becomes the tab name.</>}
              {local.display === 'cards' && <><br/><strong>Cards:</strong> First field becomes the card title.</>}
            </small>
          </div>
        </div>
      </div>
    );
  }

  // ✅ NEW: GRAPH node properties
  if (isGraph) {
    return (
      <div className="card mt-3">
        <div className="card-header">
          <div className="d-flex align-items-center">
            <span className="me-2">📊</span>
            Graph/Chart Properties
          </div>
        </div>
        <div className="card-body">

          {/* Key */}
          <div className="mb-3">
            <label className="form-label">Key Name</label>
            <input
              className="form-control form-control-sm"
              value={local.key}
              onChange={e => {
                setLocal({ ...local, key: e.target.value });
                debouncedCommit({ key: e.target.value });
              }}
              onBlur={e => immediateCommit({ key: e.target.value })}
              placeholder="e.g., scene_evidence, tension_chart"
            />
          </div>

          {/* Display As */}
          <div className="mb-3">
            <label className="form-label">Display As</label>
            <select
              className="form-select form-select-sm"
              value={local.renderAs || 'diagram'}
              onChange={e => {
                const newRenderAs = e.target.value;
                setLocal({ ...local, renderAs: newRenderAs });
                immediateCommit({ renderAs: newRenderAs });
              }}
            >
              <option value="diagram">Diagram (Mermaid.js)</option>
              <option value="chart">Data Chart (Chart.js)</option>
            </select>
          </div>

          {/* Chart Style (conditional) */}
          {local.renderAs === 'chart' && (
            <div className="mb-3">
              <label className="form-label">Chart Style</label>
              <select
                className="form-select form-select-sm"
                value={local.chartType || 'line'}
                onChange={e => {
                  const newChartType = e.target.value;
                  setLocal({ ...local, chartType: newChartType });
                  immediateCommit({ chartType: newChartType });
                }}
              >
                <option value="line">Line Graph</option>
                <option value="bar">Bar Chart</option>
                <option value="radar">Radar Chart</option>
                <option value="pie">Pie Chart</option>
                <option value="doughnut">Doughnut Chart</option>
              </select>
            </div>
          )}

          {/* Instruction */}
          <div className="mb-3">
            <label className="form-label">
              Instruction
              <Dropdown className="d-inline ms-2" onSelect={insertVar}>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Insert Var
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {variables.map(v => (
                    <Dropdown.Item key={v.key} eventKey={v.key}>
                      {v.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </label>
            <textarea
              ref={textareaRef}
              className="form-control form-control-sm"
              rows="4"
              placeholder={
                local.renderAs === 'chart' 
                  ? "Describe the data chart to generate (e.g., 'Show the tension level over story beats')"
                  : "Describe the diagram to generate (e.g., 'Character relationship flowchart')"
              }
              value={local.instruction || ''}
              onChange={e => {
                setLocal({ ...local, instruction: e.target.value });
                debouncedCommit({ instruction: e.target.value });
              }}
              onBlur={e => immediateCommit({ instruction: e.target.value })}
            />
            <small className="form-text text-muted">
              {local.renderAs === 'chart' 
                ? "For charts, describe what data should be plotted and how it should be structured."
                : "For diagrams, describe the relationships or process flow to illustrate."
              }
            </small>
          </div>
        </div>
      </div>
    );
  }

  return null;
}